/*
 * Copyright Mission 42 Ventures, Inc. All rights reserved.
 * Unauthorized copying, distribution, or use is prohibited.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';

// Mirrors LoginLookup in the backend's teamAdmin extension.
export type LoginLookup = {
  login: string;
  status: 'found' | 'missing' | 'invalid' | 'unavailable';
  canonicalLogin?: string;
  name?: string | null;
  accountType?: string;
  orgMembership: 'member' | 'pending' | 'outside' | 'unknown';
};

export type LoginVerdict = {
  severity: 'ok' | 'warn' | 'error' | 'none';
  text: string;
  // Whether this login should block staging. Only a login that provably cannot
  // reach a GitHub account blocks; "exists but not in the org" does not, because
  // a brand-new hire legitimately isn't in the org yet and refusing them would
  // break the most common reason to touch this page.
  blocking: boolean;
};

const NONE: LoginVerdict = { severity: 'none', text: '', blocking: false };

/**
 * Turns one lookup into the line rendered under the field. Pure, so the copy
 * (and which states block) is unit-tested without a network or a DOM.
 */
export function loginVerdict(
  check: LoginLookup | undefined,
  org: string,
): LoginVerdict {
  if (!check) return NONE;
  const who = check.name || check.canonicalLogin || check.login;
  // GitHub logins are case-insensitive but the Terraform list is not, so a
  // differently-cased spelling would land the same person in the list twice.
  const spelling =
    check.canonicalLogin && check.canonicalLogin !== check.login
      ? ` Staging GitHub's spelling, "${check.canonicalLogin}".`
      : '';

  switch (check.status) {
    case 'invalid':
      return {
        severity: 'error',
        text: `"${check.login}" can't be a GitHub login — letters, numbers and single hyphens only.`,
        blocking: true,
      };
    case 'missing':
      return {
        severity: 'error',
        text: `No GitHub account named "${check.login}".`,
        blocking: true,
      };
    case 'unavailable':
      return {
        severity: 'warn',
        text: `Couldn't reach GitHub to check "${check.login}" — it's checked again before the PR opens.`,
        blocking: false,
      };
    default:
      break;
  }

  switch (check.orgMembership) {
    case 'member':
      return {
        severity: 'ok',
        text: `${who} — member of ${org}.${spelling}`,
        blocking: false,
      };
    case 'pending':
      return {
        severity: 'warn',
        text: `${who} has a pending ${org} invitation — Terraform adds them to the team once it's accepted.${spelling}`,
        blocking: false,
      };
    case 'outside':
      return {
        severity: 'warn',
        // No "they'll need an invite" advice: atlantis issues the org
        // invitation itself when the plan applies, so telling the admin to go
        // do it by hand would be wrong.
        text: `${who} exists on GitHub but isn't in ${org} yet — fine for a new hire.${spelling}`,
        blocking: false,
      };
    default:
      // Found the account, but the App holds no Members:read on this org, so we
      // make no claim about their standing in it.
      return {
        severity: 'ok',
        text: `${who} exists on GitHub.${spelling}`,
        blocking: false,
      };
  }
}

const DEBOUNCE_MS = 400;
const MAX_PER_REQUEST = 25;
/**
 * How long a FAILED check is allowed to stick.
 *
 * Resolved verdicts stay for the session, but 'unavailable' is not a verdict --
 * it means we could not reach GitHub. Without an expiry, one transient failure
 * pinned that login as unavailable for the rest of the tab (the effect only
 * fetches logins absent from the cache), which quietly defeated the backend's
 * deliberate choice never to cache a failure, and made the field's "checked
 * again" promise untrue in-session.
 */
const FAILURE_TTL_MS = 15_000;
/** Bound the cache so a long session of typing cannot grow it without limit. */
const CACHE_MAX = 500;

type CacheEntry = { v: LoginLookup; ts: number };

// Shared across mounts: expanding the same team twice, or typing a login the
// new-team form already checked, shouldn't re-hit the backend.
const cache = new Map<string, CacheEntry>();
const keyFor = (org: string, login: string) =>
  `${org}:${login.trim().toLowerCase()}`;

/**
 * Is this entry still usable? Pure, so the expiry rule is testable without
 * faking timers around the hook.
 */
export const isCacheEntryFresh = (entry: CacheEntry, now: number): boolean =>
  entry.v.status !== 'unavailable' || now - entry.ts < FAILURE_TTL_MS;

const readCache = (key: string): LoginLookup | undefined => {
  const hit = cache.get(key);
  if (!hit) return undefined;
  if (isCacheEntryFresh(hit, Date.now())) return hit.v;
  cache.delete(key);
  return undefined;
};

const writeCache = (key: string, v: LoginLookup) => {
  if (cache.size >= CACHE_MAX) cache.clear();
  cache.set(key, { v, ts: Date.now() });
};

/**
 * Checks a set of logins against GitHub (via the gated team-admin backend) and
 * returns the verdicts, keyed by lowercased login.
 *
 * Debounced, deduped and cached: safe to call with a value that changes on every
 * keystroke. `checking` is true only while a request for the current input is
 * in flight, so the caller can hold submission rather than stage an unverified
 * login.
 */
export const useGithubLogins = (
  logins: readonly string[],
  org: string,
): { checks: Map<string, LoginLookup>; checking: boolean } => {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const [, bump] = useState(0);
  const [checking, setChecking] = useState(false);

  // Stable identity for the effect: the sorted, deduped, non-empty set.
  const wanted = useMemo(() => {
    const seen = new Set<string>();
    for (const l of logins) {
      const t = l.trim();
      if (t) seen.add(t);
    }
    return [...seen].sort();
  }, [logins]);
  const wantedKey = wanted.join(',');

  // Latest request wins: a stale response must not overwrite a newer verdict.
  const generation = useRef(0);

  useEffect(() => {
    // Freshness, not mere presence: an expired failure must be retried.
    const missing = wanted.filter(l => readCache(keyFor(org, l)) === undefined);
    if (missing.length === 0 || !org) {
      setChecking(false);
      return undefined;
    }
    const gen = generation.current + 1;
    generation.current = gen;
    setChecking(true);
    // Set on teardown so a response that lands after the field unmounts writes
    // to the (module-level) cache but never to dead component state.
    let cancelled = false;

    const timer = setTimeout(async () => {
      try {
        const base = await discoveryApi.getBaseUrl('team-admin');
        // The backend caps a request at MAX_PER_REQUEST; chunk rather than
        // silently dropping the tail.
        for (let i = 0; i < missing.length; i += MAX_PER_REQUEST) {
          const batch = missing.slice(i, i + MAX_PER_REQUEST);
          const url = `${base}/github-users?org=${encodeURIComponent(
            org,
          )}&logins=${encodeURIComponent(batch.join(','))}`;
          const res = await fetchApi.fetch(url);
          if (!res.ok) throw new Error(`lookup failed: ${res.status}`);
          const json = (await res.json()) as { results?: LoginLookup[] };
          for (const r of json.results ?? []) {
            if (r && typeof r.login === 'string') {
              writeCache(keyFor(org, r.login), r);
            }
          }
          if (cancelled || generation.current !== gen) return;
        }
      } catch {
        // Never leave the field spinning on a failure: mark the batch
        // unavailable so the verdict says "couldn't check" instead of nothing.
        // Recorded so the field says "couldn't check" rather than nothing, but
        // it expires (FAILURE_TTL_MS) so the next keystroke retries instead of
        // reading a stale failure for the rest of the session.
        for (const l of missing) {
          if (readCache(keyFor(org, l)) === undefined) {
            writeCache(keyFor(org, l), {
              login: l,
              status: 'unavailable',
              orgMembership: 'unknown',
            });
          }
        }
      } finally {
        if (!cancelled && generation.current === gen) {
          setChecking(false);
          bump(n => n + 1);
        }
      }
    }, DEBOUNCE_MS);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // wantedKey stands in for the `wanted` array's contents.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wantedKey, org, discoveryApi, fetchApi]);

  const checks = useMemo(() => {
    const out = new Map<string, LoginLookup>();
    for (const l of wanted) {
      const hit = readCache(keyFor(org, l));
      if (hit) out.set(l.toLowerCase(), hit);
    }
    return out;
    // Recomputed when the input set changes or a response bumps the counter.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wantedKey, org, checking]);

  return { checks, checking };
};

/** Test seam: drops the cross-mount lookup cache. */
export const __clearLoginCache = () => cache.clear();
